import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Mic, Send } from "lucide-react";

const TOPICS = [
  "Security Awareness Culture",
  "Tabletop Design & Facilitation",
  "DFIR → GRC Career Journey",
  "AI Ethics & Governance for Leaders",
  "Human-Centric Cybersecurity",
  "Custom / Other",
];

const AUDIENCE_SIZES = ["< 50", "50–200", "200–500", "500–1,000", "1,000+"];

export default function BookSpeaker() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    topic: TOPICS[0],
    event_date: "",
    audience_size: AUDIENCE_SIZES[1],
    location: "",
    budget: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/booking", form);
      toast.success("Booking request received. I'll be in touch within 3 business days.");
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-32 text-center" data-testid="booking-thank-you">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent mb-6">[ received ]</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Thank you.</h1>
        <p className="mt-6 text-muted-foreground text-lg">
          Your request landed with a filter-friendly subject line in my inbox. I'll respond within three business days.
        </p>
        <Link to="/speaking" className="mt-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] link-underline">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to talks
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="booking-page">
      <Link to="/speaking" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground link-underline">
        <ArrowLeft className="w-3.5 h-3.5" /> Speaking
      </Link>
      <div className="mt-8 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
        <Mic className="w-3.5 h-3.5" /> [ book me to speak ]
      </div>
      <motion.h1
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl"
      >
        Tell me about your stage.
      </motion.h1>
      <p className="mt-6 text-muted-foreground text-lg max-w-2xl">
        Every talk is written for a specific audience. Share the shape of your event and I'll come back with topics that fit.
      </p>

      <form onSubmit={submit} className="mt-12 space-y-6" data-testid="booking-form">
        <div className="grid sm:grid-cols-2 gap-6">
          <Input label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="booking-name-input" required />
          <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="booking-email-input" required />
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <Input label="Organization" value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} testid="booking-org-input" />
          <Input label="Event location (city / virtual)" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="booking-location-input" />
        </div>
        <div className="grid sm:grid-cols-3 gap-6">
          <Select label="Topic" value={form.topic} onChange={(v) => setForm({ ...form, topic: v })} options={TOPICS} testid="booking-topic-select" />
          <Input label="Event date" type="date" value={form.event_date} onChange={(v) => setForm({ ...form, event_date: v })} testid="booking-date-input" required />
          <Select label="Audience size" value={form.audience_size} onChange={(v) => setForm({ ...form, audience_size: v })} options={AUDIENCE_SIZES} testid="booking-audience-select" />
        </div>
        <Input label="Budget (optional)" value={form.budget} onChange={(v) => setForm({ ...form, budget: v })} testid="booking-budget-input" placeholder="e.g. $3–5k, honorarium, TBD" />
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Anything else? (optional)</label>
          <textarea
            rows={5}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            data-testid="booking-message-input"
            placeholder="Audience, format, adjacent sessions, whatever's helpful."
            className="mt-2 w-full bg-transparent border border-border rounded-xl px-4 py-3 outline-none focus:border-foreground transition-colors resize-none"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="booking-submit-btn"
          className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-foreground text-background font-mono text-xs uppercase tracking-[0.2em] disabled:opacity-50"
        >
          <Send className="w-3.5 h-3.5" />
          {loading ? "Sending…" : "Send booking request"}
        </button>
      </form>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid, required, placeholder }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{label}</label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-2 w-full bg-transparent border-b border-border focus:border-foreground outline-none py-3 transition-colors"
      />
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-2 w-full bg-transparent border-b border-border focus:border-foreground outline-none py-3 transition-colors"
      >
        {options.map((o) => <option key={o} value={o} className="bg-background text-foreground">{o}</option>)}
      </select>
    </div>
  );
}
